/* eslint-disable react/jsx-no-bind -- Controller's render prop + per-field handlers cannot be
   hoisted to useCallback (they close over the render-prop's `field`); same precedent as the
   other RHF wrappers. */
import Check from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import React from 'react'
import { Controller } from 'react-hook-form'

import HHTextField from './HHTextField'
import SelectItem from './SelectItem'

import type { SxProps, Theme } from '@mui/material/styles'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'

// Untyped .js children: the one boundary cast lives here instead of at every call site.
const HHTextFieldUntyped = HHTextField as React.ElementType
const SelectItemUntyped = SelectItem as React.ElementType

export type HHFormSelectOption = {
  key?: string
  value?: unknown
  label?: React.ReactNode
  disabled?: boolean
} & Record<string, unknown>

export type HHFormSelectFieldProps<TField extends FieldValues = FieldValues> = {
  name: FieldPath<TField>
  control: Control<TField>
  options: ReadonlyArray<HHFormSelectOption>
  sx?: SxProps<Theme>
  disabled?: boolean
  label?: string
  placeholder?: string
  size?: 'small' | 'medium'
  autocomplete?: string
  variant?: 'filled' | 'outlined' | 'standard'
  rules?: RegisterOptions<TField>
  required?: boolean
  fullWidth?: boolean
  InputProps?: Record<string, unknown>
  SelectProps?: Record<string, unknown>
  getOptionValue?: (option: HHFormSelectOption) => unknown
  getOptionLabel?: (option: HHFormSelectOption) => React.ReactNode
  multiple?: boolean
  onChange?: (value: unknown) => void
  // false = label inside the field (new style); true = legacy label above the field.
  deprecatedLabel?: boolean
  helperText?: string
  error?: string
}

const defaultGetOptionValue = (option: HHFormSelectOption) => option?.value
const defaultGetOptionLabel = (option: HHFormSelectOption) => option?.label

const HHFormSelectField = <TField extends FieldValues = FieldValues>({
  sx = undefined,
  name,
  control,
  disabled = false,
  label = undefined,
  placeholder = '',
  size = 'medium',
  autocomplete = 'off',
  variant = 'outlined',
  rules = undefined,
  required = false,
  fullWidth = false,
  InputProps = {},
  options,
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  multiple = false,
  onChange,
  SelectProps = {},
  ...rest
}: HHFormSelectFieldProps<TField>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const { ref, onChange: onChangeHookForm, onBlur, ...fieldRest } = field
        const handleChangeWrapper = (event: React.ChangeEvent<HTMLInputElement>) => {
          const raw: unknown = event.target.value
          // MUI's multiple-Select autofill delivers a comma-joined string.
          const nextValue = multiple && typeof raw === 'string' ? raw.split(',') : raw
          onChangeHookForm(nextValue)
          onChange?.(nextValue)
        }
        return (
          <HHTextFieldUntyped
            autoComplete={autocomplete}
            disabled={disabled}
            InputProps={InputProps}
            inputRef={ref}
            label={required && label ? `${label}*` : label}
            placeholder={placeholder}
            required={required || rules?.required === true}
            size={size}
            sx={sx}
            variant={variant}
            onBlur={onBlur}
            onChange={handleChangeWrapper}
            {...rest}
            {...fieldRest}
            select
            fullWidth={fullWidth}
            SelectProps={{ ...SelectProps, multiple }}
          >
            {options.map(option => {
              const value = getOptionValue(option)
              const isSelected = !multiple ? value === field?.value : Array.isArray(field?.value) && field.value.includes(value)
              return (
                <SelectItemUntyped
                  key={option?.key || `${name}-option-${String(getOptionLabel(option))}`}
                  disableGutters
                  disabled={Boolean(option?.disabled)}
                  value={value}
                >
                  {getOptionLabel(option)}
                  {isSelected ? <Check sx={{ display: 'none' }} /> : <Box width={24} />}
                </SelectItemUntyped>
              )
            })}
          </HHTextFieldUntyped>
        )
      }}
      rules={rules}
    />
  )
}

export default HHFormSelectField
